/**
 * Prüftunnel: ein Schritt je Bildschirm.
 *
 * Der Zweck bestimmt die Form: ein unerfahrener Mitarbeiter soll das Gerät
 * abarbeiten können, ohne zu wissen, worauf zu achten ist. Deshalb steht auf
 * jedem Bildschirm genau eine Frage, groß, mit dem Hinweis des Prüfers
 * darunter – und nicht eine Liste, in der man sich verliert.
 *
 * Am Ende zeigt der Tunnel, was er aus den Angaben ableitet, samt Begründung.
 * Übernommen wird erst auf Knopfdruck: der Prüfer trägt die Verantwortung,
 * nicht der Ablauf.
 */

import { useMemo, useState } from 'react'
import {
  SCHRITTARTTEXT,
  sollbereichText,
  useSchrittErledigen,
  useTunnel,
  useTunnelAbschliessen,
  weichtAb,
  zahl,
  type Schrittergebnis,
  type Tunnelschritt,
  type TunnelVorschlag,
} from '../api/tunnel'
import { STUFENTEXT, type Geraetebefund } from '../api/typen'
import { Blatt } from '../bausteine/Blatt'
import { Fehlerhinweis, Ladeanzeige, Leerhinweis, Stufenzeichen } from '../bausteine/Hinweise'

type Eigenschaften = {
  pruefungId: string
  befund: Geraetebefund
  schliessen: () => void
  /** Nach dem Übernehmen: Bogen neu laden und weiterspringen. */
  fertig: () => void
  beimFoto: (datei: File) => Promise<void>
}

export function Tunnel({ pruefungId, befund, schliessen, fertig, beimFoto }: Eigenschaften) {
  const stand = useTunnel(pruefungId, befund.id)
  const erledigen = useSchrittErledigen(pruefungId, befund.id)
  const abschliessen = useTunnelAbschliessen(pruefungId, befund.id)

  const [platz, setzePlatz] = useState(0)
  const [amEnde, setzeAmEnde] = useState(false)
  const [vorschlag, setzeVorschlag] = useState<TunnelVorschlag | null>(null)
  const [fehler, setzeFehler] = useState<unknown>(null)

  const schritte = useMemo(() => stand.data?.vorlage?.schritte ?? [], [stand.data])
  const ergebnisse = useMemo(() => {
    const karte = new Map<string, Schrittergebnis>()
    for (const e of stand.data?.ergebnisse ?? []) karte.set(e.schritt_id, e)
    return karte
  }, [stand.data])

  if (stand.isLoading) {
    return (
      <Blatt titel={befund.bezeichnung} schliessen={schliessen}>
        <Ladeanzeige text="Lädt den Ablauf …" />
      </Blatt>
    )
  }

  if (!stand.data?.vorlage) {
    return (
      <Blatt titel={befund.bezeichnung} schliessen={schliessen}>
        <Leerhinweis
          titel="Kein Ablauf hinterlegt"
          text={
            <>
              Für den Gerätetyp „{befund.geraetetyp ?? '—'}" gibt es keinen Schrittablauf.
              Das Gerät lässt sich wie gewohnt von Hand beurteilen; einen Ablauf legt die
              Verwaltung unter Bausteine an.
            </>
          }
        />
      </Blatt>
    )
  }

  const schritt = schritte[platz]
  const anteil = schritte.length ? Math.round(((platz + (amEnde ? 1 : 0)) / schritte.length) * 100) : 0

  // ── Abschlussbildschirm ───────────────────────────────────────
  if (amEnde) {
    const ergebnis = vorschlag
    return (
      <Blatt
        titel="Ergebnis des Ablaufs"
        untertitel={befund.bezeichnung}
        schliessen={schliessen}
        fuss={
          <div className="flex gap-2">
            <button
              type="button"
              className="knopf-haupt flex-1"
              disabled={abschliessen.isPending || !ergebnis?.vollstaendig}
              onClick={() => {
                setzeFehler(null)
                // Leerer Rumpf: der Server nimmt seinen eigenen Vorschlag.
                // Abweichen geht über die Beurteilung von Hand.
                abschliessen.mutate({}, { onSuccess: fertig, onError: setzeFehler })
              }}
            >
              {abschliessen.isPending ? 'Übernimmt …' : 'Als Beurteilung übernehmen'}
            </button>
            <button
              type="button"
              className="knopf-still px-4"
              onClick={() => {
                setzeAmEnde(false)
                setzePlatz(Math.max(0, schritte.length - 1))
              }}
            >
              Zurück
            </button>
          </div>
        }
      >
        <>
          {ergebnis ? (
            <>
              <div className="karte mb-4 flex items-center gap-3">
                <Stufenzeichen stufe={ergebnis.stufe} />
                <div>
                  <div className="text-lg font-bold">{STUFENTEXT[ergebnis.stufe]}</div>
                  {ergebnis.stufe === '3' && (
                    <div className="font-semibold text-stufe-3">
                      Das Gerät wird gesperrt und der Kunde ist unverzüglich zu
                      informieren.
                    </div>
                  )}
                </div>
              </div>

              {ergebnis.mangel && (
                <div className="mb-4">
                  <label>Mangel, wie er im Bericht steht</label>
                  <div className="karte whitespace-pre-wrap">{ergebnis.mangel}</div>
                </div>
              )}

              {ergebnis.normverweis && (
                <p className="mb-4 text-base text-slate-700">
                  Normverweis: {ergebnis.normverweis}
                </p>
              )}

              <div className="mb-4">
                <label>Wie der Ablauf darauf kommt</label>
                <ul className="karte list-disc space-y-1 pl-5 text-base">
                  {ergebnis.begruendung.map((zeile, i) => (
                    <li key={i}>{zeile}</li>
                  ))}
                </ul>
              </div>

              {!ergebnis.vollstaendig && (
                <div className="rounded-feld border-2 border-stufe-2 bg-stufe-2-grund p-3">
                  <p className="font-semibold text-stufe-2">
                    Es sind noch Pflichtschritte offen. Der Ablauf lässt sich erst
                    übernehmen, wenn alle abgearbeitet sind – sonst stünde im Bericht eine
                    Beurteilung, für die die Grundlage fehlt.
                  </p>
                </div>
              )}

              <p className="mt-4 text-base text-slate-700">
                Mit dem Übernehmen gilt das Gerät als beurteilt. Wollen Sie abweichen,
                schließen Sie den Ablauf und beurteilen von Hand – die Schrittergebnisse
                bleiben dabei erhalten.
              </p>
            </>
          ) : (
            <Ladeanzeige text="Bildet das Ergebnis …" />
          )}

          <Fehlerhinweis fehler={fehler ?? abschliessen.error} />
        </>
      </Blatt>
    )
  }

  // ── Schrittbildschirm ─────────────────────────────────────────
  return (
    <Blatt
      titel={`Schritt ${platz + 1} von ${schritte.length}`}
      untertitel={`${befund.bezeichnung} · ${stand.data.vorlage.name}`}
      schliessen={schliessen}
    >
      <SchrittAnsicht
        schritt={schritt}
        vorhanden={ergebnisse.get(schritt.id) ?? null}
        laeuft={erledigen.isPending}
        fehler={fehler ?? erledigen.error}
        anteil={anteil}
        istLetzter={platz === schritte.length - 1}
        zurueck={platz > 0 ? () => setzePlatz(platz - 1) : undefined}
        beimFoto={beimFoto}
        weiter={(eingabe) => {
          setzeFehler(null)
          erledigen.mutate(
            { schrittId: schritt.id, eingabe },
            {
              onSuccess: (neuerVorschlag) => {
                setzeVorschlag(neuerVorschlag)
                if (platz === schritte.length - 1) setzeAmEnde(true)
                else setzePlatz(platz + 1)
              },
              onError: setzeFehler,
            },
          )
        }}
        ueberspringen={
          schritt.pflicht
            ? undefined
            : () => {
                if (platz === schritte.length - 1) setzeAmEnde(true)
                else setzePlatz(platz + 1)
              }
        }
      />
    </Blatt>
  )
}

/** Ein Schritt. Die Eingabe hängt an der Art. */
function SchrittAnsicht({
  schritt,
  vorhanden,
  laeuft,
  fehler,
  anteil,
  istLetzter,
  zurueck,
  weiter,
  ueberspringen,
  beimFoto,
}: {
  schritt: Tunnelschritt
  vorhanden: { in_ordnung: boolean | null; messwert: number | null; notiz: string | null } | null
  laeuft: boolean
  fehler: unknown
  anteil: number
  istLetzter: boolean
  zurueck?: () => void
  weiter: (eingabe: { in_ordnung?: boolean | null; messwert?: number | null; notiz?: string | null }) => void
  ueberspringen?: () => void
  beimFoto: (datei: File) => Promise<void>
}) {
  const [messwert, setzeMesswert] = useState(
    vorhanden?.messwert !== null && vorhanden?.messwert !== undefined
      ? String(vorhanden.messwert).replace('.', ',')
      : '',
  )
  const [notiz, setzeNotiz] = useState(vorhanden?.notiz ?? '')
  const [fotoLaeuft, setzeFotoLaeuft] = useState(false)
  const [fotoDa, setzeFotoDa] = useState(false)

  const zahlWert = messwert.trim() ? Number(messwert.replace(',', '.')) : null
  const abweichend = weichtAb(schritt, zahlWert)
  const messwertFehlt = schritt.art === 'messwert' && (zahlWert === null || Number.isNaN(zahlWert))

  return (
    <>
      <div className="mb-4 h-2 w-full rounded bg-slate-200">
        <div className="h-full rounded bg-marke-600" style={{ width: `${anteil}%` }} />
      </div>

      <p className="mb-1 text-sm font-semibold uppercase tracking-wide text-slate-600">
        {SCHRITTARTTEXT[schritt.art]}
        {!schritt.pflicht && ' · freiwillig'}
      </p>
      <h3 className="mb-2 text-2xl font-bold leading-tight">{schritt.titel}</h3>

      {schritt.hinweis && (
        <p className="mb-4 rounded-feld border-2 border-marke-100 bg-marke-50 p-3 text-base">
          {schritt.hinweis}
        </p>
      )}

      {schritt.normverweis && (
        <p className="mb-4 text-base text-slate-600">Grundlage: {schritt.normverweis}</p>
      )}

      {/* ── Messwert ─────────────────────────────────────────── */}
      {schritt.art === 'messwert' && (
        <div className="mb-4">
          <label htmlFor="messwert">
            {schritt.messgroesse}
            {schritt.einheit ? ` in ${schritt.einheit}` : ''}
          </label>
          <input
            id="messwert"
            type="text"
            inputMode="decimal"
            value={messwert}
            onChange={(e) => setzeMesswert(e.target.value)}
            placeholder="0"
            className="text-2xl font-bold"
            autoFocus
          />
          <p className="mt-1 text-base text-slate-700">Sollwert: {sollbereichText(schritt)}</p>

          {/* Sofortige Rückmeldung, damit der Prüfer nicht erst am Ende sieht,
              dass der Wert eine Stufe auslöst. Verbindlich rechnet der Server. */}
          {zahlWert !== null && !Number.isNaN(zahlWert) && (
            <p
              className={`mt-2 rounded-feld border-2 p-2 font-semibold ${
                abweichend
                  ? 'border-stufe-2 bg-stufe-2-grund text-stufe-2'
                  : 'border-stufe-ob bg-stufe-ob-grund text-stufe-ob'
              }`}
            >
              {abweichend
                ? `${zahl(zahlWert)}${schritt.einheit ? ' ' + schritt.einheit : ''} liegt außerhalb – ` +
                  `das ergibt Stufe ${schritt.stufe_bei_abweichung ?? '2'}.`
                : `${zahl(zahlWert)}${schritt.einheit ? ' ' + schritt.einheit : ''} liegt im Sollbereich.`}
            </p>
          )}
        </div>
      )}

      {/* ── Foto ─────────────────────────────────────────────── */}
      {schritt.art === 'foto' && (
        <div className="mb-4">
          <input
            id="tunnelfoto"
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={async (e) => {
              const datei = e.target.files?.[0]
              e.target.value = ''
              if (!datei) return
              setzeFotoLaeuft(true)
              try {
                await beimFoto(datei)
                setzeFotoDa(true)
              } finally {
                setzeFotoLaeuft(false)
              }
            }}
          />
          <button
            type="button"
            className={fotoDa ? 'knopf-still w-full' : 'knopf-zweit w-full'}
            onClick={() => document.getElementById('tunnelfoto')?.click()}
            disabled={fotoLaeuft}
            style={{ minHeight: '64px' }}
          >
            {fotoLaeuft
              ? 'Verarbeitet …'
              : fotoDa
                ? 'Aufnahme gespeichert – weitere aufnehmen'
                : 'Aufnehmen'}
          </button>
        </div>
      )}

      {/* ── Notiz für alle Arten ─────────────────────────────── */}
      <div className="mb-4">
        <label htmlFor="notiz">
          Notiz{' '}
          <span className="font-normal text-slate-600">
            {schritt.art === 'sicht' || schritt.art === 'bestaetigung'
              ? '(bei „nicht in Ordnung" bitte ausfüllen – der Text landet im Bericht)'
              : '(freiwillig)'}
          </span>
        </label>
        <textarea
          id="notiz"
          value={notiz}
          onChange={(e) => setzeNotiz(e.target.value)}
          style={{ minHeight: '72px' }}
        />
      </div>

      <Fehlerhinweis fehler={fehler} />

      {/* ── Entscheidung ─────────────────────────────────────── */}
      <div className="mt-4 space-y-2">
        {schritt.art === 'sicht' || schritt.art === 'bestaetigung' || schritt.art === 'hinweis' ? (
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              className="knopf-zweit border-stufe-ob text-stufe-ob hover:bg-stufe-ob-grund"
              style={{ minHeight: '64px' }}
              disabled={laeuft}
              onClick={() => weiter({ in_ordnung: true, notiz: notiz.trim() || null })}
            >
              In Ordnung
            </button>
            <button
              type="button"
              className="knopf-warnung"
              style={{ minHeight: '64px' }}
              disabled={laeuft}
              onClick={() => weiter({ in_ordnung: false, notiz: notiz.trim() || null })}
            >
              Nicht in Ordnung
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="knopf-haupt w-full"
            style={{ minHeight: '64px' }}
            disabled={laeuft || messwertFehlt}
            onClick={() =>
              weiter({
                messwert: schritt.art === 'messwert' ? zahlWert : null,
                in_ordnung: schritt.art === 'foto' ? true : null,
                notiz: notiz.trim() || null,
              })
            }
          >
            {laeuft ? 'Speichert …' : istLetzter ? 'Übernehmen und Ergebnis' : 'Weiter'}
          </button>
        )}

        {messwertFehlt && (
          <p className="text-base text-stufe-2">
            Ohne Messwert geht es nicht weiter – der Sollbereich lässt sich sonst nicht
            prüfen.
          </p>
        )}

        <div className="flex gap-2">
          {zurueck && (
            <button type="button" className="knopf-still flex-1" onClick={zurueck}>
              Zurück
            </button>
          )}
          {ueberspringen && (
            <button type="button" className="knopf-still flex-1" onClick={ueberspringen}>
              Überspringen
            </button>
          )}
        </div>
      </div>
    </>
  )
}
